<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, watch } from 'vue';
import MultiSelect from 'primevue/multiselect';
import TreeSelect from 'primevue/treeselect';
import { Activity, Overview, Payment, TimeUsage } from './components';
import AppTabMenu from '@/components/AppTabMenu';
import {
  useAttendanceStore,
  useAuthStore,
  useCustomerSuccessPartnerStore,
  useEvaluationStore,
  useOrganizationStore,
} from '@/store';
import { PERMISSIONS, TENANT_SETTINGS } from '@/enum';
import { useRouting, useTenant, can, usePersonTypes, useRequestCancel } from '@/composables';

const authStore = useAuthStore();
const organizationStore = useOrganizationStore();
const requestCancel = useRequestCancel();
const evaluationStore = useEvaluationStore();
const attendanceStore = useAttendanceStore();
const customerSuccessPartnerStore = useCustomerSuccessPartnerStore();
// Disabling Compliance widget for ticket TMS-1997
// const complianceStore = useComplianceStore();
const $tenant = useTenant();
const $routing = useRouting();

const organizationsIdsSelected = computed(() => organizationStore.organizationsIdsSelected);

const isSuperAdmin = usePersonTypes.isSuperAdmin();
const canViewUtilizationWidgets =
  can([PERMISSIONS.CLINICIAN_ANALYTICS]) &&
  (isSuperAdmin || usePersonTypes.isSupportAdmin() || usePersonTypes.isBillingAdmin());

const menuItems = computed(() => {
  const items = [
    {
      route: 'overview',
      label: 'Overview',
      component: Overview,
      disabled: () => $routing.route.path === '/dashboard/overview',
    },
  ];

  if (can([PERMISSIONS.DASHBOARD_ACTIVITY_INDEX])) {
    items.push({
      route: 'activity',
      label: 'Activity',
      component: Activity,
      disabled: () => $routing.route.path === '/dashboard/activity',
    });
  }

  if (can([PERMISSIONS.PAYMENT_ME])) {
    items.push({
      route: 'payment',
      label: 'Payment',
      component: Payment,
      disabled: () => $routing.route.path === '/dashboard/payment',
    });
  }

  if ($tenant.tenantSettingEnabled(TENANT_SETTINGS.UTILIZATION_TRACKING) && can([PERMISSIONS.PAYMENT_ME])) {
    items.push({
      route: 'time-usage',
      label: 'Time Usage',
      component: TimeUsage,
      disabled: () => $routing.route.path === '/dashboard/time-usage',
    });
  }

  return items;
});

onMounted(() => init());

const init = async () => {
  await authStore.getOrganizations();

  if (organizationStore.organizationsIdsSelected.length > 0) {
    await onOrganization();
  }
};

onBeforeUnmount(() => {
  requestCancel.requestSaverAbortAll();
});

watch(
  () => authStore.impersonateToken,
  (value, oldValue) => {
    if (oldValue) {
      init();
    }
  },
);

const onOrganization = async () => {
  evaluationStore.getEvaluations(organizationsIdsSelected.value);
  attendanceStore.getAllAttendance(organizationsIdsSelected.value);

  // Disabling Compliance widget for ticket TMS-1997
  // complianceStore.getAllCompliance(undefined, organizationsIdsSelected.value);
};
</script>

<template>
  <div class="page-container dashboard-page-container">
    <div class="page-header page-header-with-tabs">
      <h1 class="pb-2">Dashboard</h1>
      <div class="w-full flex flex-wrap gap-3 justify-content-between">
        <AppTabMenu :items="menuItems" />
        <div class="inline-flex gap-2">
          <MultiSelect
            v-if="$tenant.tenantSettingEnabled(TENANT_SETTINGS.UTILIZATION_TRACKING) && canViewUtilizationWidgets"
            id="organization_profile_general_customer_success_partner_id"
            v-model="customerSuccessPartnerStore.selectedDashboardCustomerSuccessPartners"
            :options="customerSuccessPartnerStore.customerSuccessPartners"
            option-label="display_name"
            :max-selected-labels="2"
            :loading="customerSuccessPartnerStore.loading"
            placeholder="All CSPs"
          />
          <TreeSelect
            v-if="can([PERMISSIONS.USERS_ORGANIZATIONS_SHOW]) && authStore.organizationAssociated.length"
            id="parent_organization"
            v-model="organizationStore.selectedOrganizations"
            :options="authStore.organizationAssociated"
            option-label="name"
            option-value="id"
            :loading="authStore.loading"
            placeholder="All Organizations"
            style="min-width: 240px"
            class="border-transparent max-w-sm font-bold"
            scroll-height="200px"
            selection-mode="multiple"
            display="chip"
            :meta-key-selection="false"
            @change="onOrganization"
          />
        </div>
      </div>
    </div>
    <div class="page-content">
      <RouterView />
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-treeselect) {
  .p-treeselect-label {
    font-weight: bold;
    &.p-placeholder {
      color: $grayDark;
    }
  }
  .p-treeselect-label-container {
    min-width: 15rem;
  }
}
</style>
